import * as Api from '@ViewModels';
import { THEME } from '@Theme';
import { css } from 'aphrodite';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { invalidateAccountAdminQuery } from '../../../../queries/Admin/Account/useAdminAccountQuery';
import { AIStarsIcon } from '../../../../web/components/svgs/icons/AIStars';
import { BrandSettingsModal } from '../../../components/BrandSettingsModal';
import { CustomContentPreferencesDialog } from '../../../components/CustomContentPreferencesModal';
import { GoogleReviewBrandingSettingsModal } from '../../../components/GoogleReviewBrandingSettingsModal';
import { SubverticalSettingsDialog } from '../../../components/SubverticalSelect';
import { Button } from '../../../../web/components-v2/Button';
import { AccountLogoField } from './AccountLogoField';
import { customizationsCardStyles as s } from './customizationsCardStyles';
import { OverviewCard } from './OverviewCard';
import { overviewFieldStyles } from './overviewFieldStyles';

/** Accent color for the card's ModuleTag header. */
const CARD_COLOR = THEME.colors.tertiary.main;

export interface ICustomizationsCardProps {
	/** The persisted account; passed straight to the settings modals and the logo field. */
	account: Api.IAccount;
}

/**
 * A single settings field: uppercase label on top, "Manage" link below (matching the read-mode
 * field layout of the other Overview cards). The link only renders when the admin holds
 * `ModifyAccount`; otherwise the field shows a muted read-only marker.
 */
const SettingsRow = ({
	label,
	canManage,
	onManage,
	icon,
}: {
	label: string;
	canManage: boolean;
	onManage: () => void;
	icon?: React.ReactNode;
}) => (
	<div className={css(overviewFieldStyles.field)}>
		<span className={css(overviewFieldStyles.label)}>{label}</span>
		{canManage ? (
			<Button variant='link' styles={[s.manageLink]} onClick={onManage}>
				{icon}
				<span>Manage</span>
			</Button>
		) : (
			<span className={css(overviewFieldStyles.value, overviewFieldStyles.valueMuted)}>View only</span>
		)}
	</div>
);

/**
 * True only for `http://` / `https://` URLs (case-insensitive, after trim). The blog domain is
 * admin-entered, and rendering an unvetted `javascript:` / `data:` value as an href would be
 * admin-scope stored XSS — such a value is shown as plain text instead of a link.
 */
const isSafeHttpUrl = (url: string | undefined): boolean => /^https?:\/\//i.test((url ?? '').trim());

/**
 * "Blog URL" field — the customer-facing link to their blog. Only rendered when the blog feature is
 * enabled (legacy parity); shows a dash when the feature is on but no domain has been set yet.
 */
const BlogUrlField = ({ domain }: { domain?: string }) => (
	<div className={css(overviewFieldStyles.field)}>
		<span className={css(overviewFieldStyles.label)}>Blog URL</span>
		{domain ? (
			isSafeHttpUrl(domain) ? (
				<a className={css(s.blogLink)} href={domain} target='_blank' rel='noreferrer'>
					{domain}
				</a>
			) : (
				<span className={css(overviewFieldStyles.value)}>{domain}</span>
			)
		) : (
			<span className={css(overviewFieldStyles.value, overviewFieldStyles.valueMuted)}>-</span>
		)}
	</div>
);

/**
 * Account Overview "Customizations" card. No MobX / no viewmodels — renders the logo field
 * plus "Manage" links that open the existing settings modals (each takes a plain `account`).
 * Modal open state is local; on close the admin account query is invalidated so the parent
 * refetches any changes (replacing the legacy `loadAccount()` / `applyAccount()` calls). The
 * Customer Assistant row navigates via the router. Read-only: the card is never editable, so
 * `OverviewCard` renders just the "Customizations" header with no Edit/Save/Cancel chrome.
 */
export const CustomizationsCard = ({ account }: ICustomizationsCardProps) => {
	const canModifyAccount = useAdminUserSession().hasPermission(AdminPermission.ModifyAccount);
	const history = useHistory();

	const [showSubverticalModal, setShowSubverticalModal] = useState(false);
	const [showBrandSettingsModal, setShowBrandSettingsModal] = useState(false);
	const [showCustomContentModal, setShowCustomContentModal] = useState(false);
	const [showGoogleReviewModal, setShowGoogleReviewModal] = useState(false);

	// Closing any settings modal may have persisted a change; refetch so the read view reflects it.
	const closeAndRefresh = (close: () => void) => {
		close();
		invalidateAccountAdminQuery();
	};

	return (
		<OverviewCard
			title='Customizations'
			color={CARD_COLOR}
			canEdit={false}
			isEditMode={false}
			isSaving={false}
			onEdit={() => undefined}
			onSave={() => undefined}
			onCancel={() => undefined}
		>
			<AccountLogoField account={account} />

			<div className={css(s.grid)}>
				<SettingsRow
					label='Industry & Subvertical Settings'
					canManage={canModifyAccount}
					onManage={() => setShowSubverticalModal(true)}
				/>
				<SettingsRow
					label='Brand Settings'
					canManage={canModifyAccount}
					onManage={() => setShowBrandSettingsModal(true)}
				/>
				<SettingsRow
					label='Custom Content Preferences'
					canManage={canModifyAccount}
					onManage={() => setShowCustomContentModal(true)}
				/>
				<SettingsRow
					label='Google Review Branding Preferences'
					canManage={canModifyAccount}
					onManage={() => setShowGoogleReviewModal(true)}
				/>
				<SettingsRow
					label='Customer Assistant'
					canManage={canModifyAccount}
					onManage={() => history.push(`/accounts/${account.id}/customer-assistant`)}
					icon={<AIStarsIcon height={16} width={16} />}
				/>
				{account.features?.blogFeature?.enabled && <BlogUrlField domain={account.features.blogFeature.domain} />}
			</div>

			{canModifyAccount && (
				<>
					<SubverticalSettingsDialog
						account={account}
						open={showSubverticalModal}
						onOpenChange={open => {
							if (!open) {
								closeAndRefresh(() => setShowSubverticalModal(false));
							} else {
								setShowSubverticalModal(true);
							}
						}}
					/>
					<CustomContentPreferencesDialog
						account={account}
						open={showCustomContentModal}
						onOpenChange={open => {
							if (!open) {
								closeAndRefresh(() => setShowCustomContentModal(false));
							} else {
								setShowCustomContentModal(true);
							}
						}}
					/>
					<BrandSettingsModal
						account={account}
						open={showBrandSettingsModal}
						onOpenChange={open => {
							if (!open) {
								closeAndRefresh(() => setShowBrandSettingsModal(false));
							} else {
								setShowBrandSettingsModal(true);
							}
						}}
					/>
					<GoogleReviewBrandingSettingsModal
						account={account}
						open={showGoogleReviewModal}
						onOpenChange={open => {
							if (!open) {
								closeAndRefresh(() => setShowGoogleReviewModal(false));
							} else {
								setShowGoogleReviewModal(true);
							}
						}}
					/>
				</>
			)}
		</OverviewCard>
	);
};
